import { useRef, useEffect, useCallback, Fragment } from 'react';
import { cn } from '@/lib/utils';
import { BuilderMessage } from '@/lib/api';
import { MessageBubble } from './MessageBubble';
import { Loader2 } from 'lucide-react';
import type { VerboseEvent } from '../hooks/useBuilderSession';
import { VerboseTimeline } from '../../../components/VerboseTimeline';

// Re-export utilities for backward compatibility (used by tests)
export { parseToolResultString, hasWarnings } from '../../../components/VerboseTimeline';
export type { ParsedToolResult } from '../../../components/VerboseTimeline';

interface ConversationPaneProps {
  messages: BuilderMessage[];
  isProcessing: boolean;
  suggestedNextSteps?: string[];
  /** Streaming text being received (nodeId -> accumulated text) */
  streamingText?: Record<string, string>;
  /** Committed verbose events keyed by assistant message timestamp */
  verboseHistory?: Record<string, VerboseEvent[]>;
  /** In-progress verbose events for the current response */
  pendingVerboseEvents?: VerboseEvent[];
  onSuggestionClick?: (suggestion: string) => void;
  /** Custom empty state content (replaces default greeting) */
  emptyStateContent?: React.ReactNode;
  /** Content rendered at the top of the message area (e.g. guided panels) */
  headerContent?: React.ReactNode;
  /** Content rendered below messages (e.g. guided controls) */
  footerContent?: React.ReactNode;
  className?: string;
}

export function ConversationPane({
  messages,
  isProcessing,
  suggestedNextSteps,
  streamingText,
  verboseHistory,
  pendingVerboseEvents,
  onSuggestionClick,
  emptyStateContent,
  headerContent,
  footerContent,
  className,
}: ConversationPaneProps) {
  // Get streaming content from 'builder' nodeId
  const currentStreamingContent = streamingText?.['builder'] || '';

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Scroll the messages container to the bottom without affecting the parent
  // horizontal scroll (which would pull mobile users back to the conversation panel).
  const scrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, []);

  // Auto-scroll to bottom when new messages arrive or streaming content updates
  useEffect(() => {
    scrollToBottom();
  }, [messages, currentStreamingContent, scrollToBottom]);

  // Scroll for verbose events separately: only when the first event appears (0 → 1+),
  // not on every subsequent addition, to avoid repeated scroll animations.
  const prevVerboseCountRef = useRef(0);
  useEffect(() => {
    const count = pendingVerboseEvents?.length || 0;
    if (count > 0 && prevVerboseCountRef.current === 0) {
      scrollToBottom();
    }
    prevVerboseCountRef.current = count;
  }, [pendingVerboseEvents?.length, scrollToBottom]);

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Header content (e.g. guided panels) */}
      {headerContent}
      {/* Messages area */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          emptyStateContent ? (
            emptyStateContent
          ) : (
            <div className="flex flex-col items-center justify-center h-full px-4">
              <div className="max-w-lg space-y-4">
                <h3 className="text-lg font-semibold text-foreground text-center">
                  Hello! I&apos;m the Fias Arche Builder.
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground text-center">
                  I help you create interactive AI-powered applications called Arches. Just describe
                  what you have in mind, and I&apos;ll help you bring it to life with the
                  appropriate UI components and workflow logic!
                </p>
                <p className="text-sm font-medium text-foreground">I can help you:</p>
                <ul className="text-sm space-y-2 text-muted-foreground list-disc pl-5">
                  <li>
                    <strong className="text-foreground">Design user interfaces</strong> using
                    components like forms, buttons, cards, tables, and more
                  </li>
                  <li>
                    <strong className="text-foreground">Create workflows</strong> that orchestrate
                    AI Entity interactions
                  </li>
                  <li>
                    <strong className="text-foreground">Build complete Arches</strong> from concept
                    to implementation
                  </li>
                  <li>
                    <strong className="text-foreground">Iterate and refine</strong> your designs
                    based on feedback
                  </li>
                  <li>
                    <strong className="text-foreground">Share and earn</strong> — use Arches
                    yourself, or make them available to others and earn based on their use
                  </li>
                </ul>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  What kind of Arche would you like to build today?
                </p>
              </div>
            </div>
          )
        ) : (
          messages.map((message, index) => (
            <Fragment key={index}>
              {/* Show committed verbose events before the assistant message they produced */}
              {message.role === 'assistant' &&
                verboseHistory?.[message.timestamp] &&
                verboseHistory[message.timestamp].length > 0 && (
                  <VerboseTimeline events={verboseHistory[message.timestamp]} />
                )}
              <MessageBubble message={message} />
            </Fragment>
          ))
        )}

        {/* Pending verbose events for in-progress response */}
        {pendingVerboseEvents && pendingVerboseEvents.length > 0 && (
          <VerboseTimeline events={pendingVerboseEvents} isPending />
        )}

        {/* Streaming response or processing indicator */}
        {isProcessing &&
          (currentStreamingContent ? (
            <MessageBubble
              message={{
                role: 'assistant',
                content: currentStreamingContent,
                timestamp: new Date().toISOString(),
              }}
              isStreaming
            />
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Fias AI is thinking...</span>
            </div>
          ))}

        <div ref={messagesEndRef} />
      </div>

      {/* Suggestions */}
      {suggestedNextSteps && suggestedNextSteps.length > 0 && !isProcessing && (
        <div className="px-4 pb-2">
          <div className="flex flex-wrap gap-2">
            {suggestedNextSteps.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => onSuggestionClick?.(suggestion)}
                className="text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-muted/80 transition-colors"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Footer content (e.g. guided controls) */}
      {footerContent}
    </div>
  );
}
